import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import BackButton from 'src/features/navigation/components/BackButton';
import { PATH } from 'src/features/navigation/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { BOOLEAN_MAP } from 'src/features/sites/utils/constants';
import { useGetZone } from 'src/features/zone/api/useGetZone';
import DeleteZoneFormModal from 'src/features/zone/components/form/DeleteZoneFormModal';
import UpdateZoneFormModal from 'src/features/zone/components/form/UpdateZoneFormModal';
import ZonePricingPlans from 'src/features/zone/components/ZonePricingPlans';
import { VISITOR_CATEGORIES_TRANSLATION_MAP, zoneTypeBadgeOptions } from 'src/features/zone/utils/constants';
import { zoneMessages } from 'src/features/zone/utils/messages';
import { ZONE_TYPE } from 'src/features/zone/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import { SectionBlock, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { Separator } from 'src/registry/ui/Separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/registry/ui/Tabs';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';
import { commonMessages, generalMessages } from 'src/utils/messages';

type BOOLEAN_TYPE = 'true' | 'false';
export default function ZoneDashboard() {
    const { formatMessage, locale } = useIntl();
    const { zoneId = '' } = useParams();
    const { parkingId } = useParking();

    const { data: zoneData } = useGetZone({ zoneId, parkingId });

    const accessSites = useHasPermissions([PERMISSION.ACCESS_SITES]);
    const managePricingPlans = useHasPermissions([PERMISSION.MANAGE_PRICING_PLANS]);
    const manageZone = useHasPermissions([PERMISSION.MANAGE_ZONES]);

    const [searchParams, setSearchParams] = useSearchParams();

    const defaultTab = 'pricing-plans';
    const tab = searchParams.get('tab') ?? defaultTab;

    const { setTitle, removeTitle } = useTitleStore();
    React.useEffect(() => {
        setTitle(formatMessage(zoneMessages.zoneDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const zoneTypeBadgeOption = zoneTypeBadgeOptions[zoneData?.type as ZONE_TYPE];

    return (
        <>
            <BackButton customRoute={`/${PATH.ADMINISTRATION}/zone`} />

            <SectionBlock>
                <div className="flex flex-row items-end justify-between p-0">
                    <span className="flex flex-row items-center">
                        <SectionBlockTitle className="mr-2">
                            {zoneData?.translations.find((translation) => translation.language === locale)?.name ?? ''}
                        </SectionBlockTitle>
                    </span>

                    {manageZone && zoneData && (
                        <div className="flex flex-row gap-2">
                            <UpdateZoneFormModal
                                selectedZone={zoneData}
                                semantics={{ entryName: formatMessage(zoneMessages.zone).toLowerCase() }}
                            >
                                <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                    <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                    <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>{' '}
                                </Button>
                            </UpdateZoneFormModal>
                            <DeleteZoneFormModal
                                selectedZone={zoneData}
                                semantics={{ entryName: formatMessage(zoneMessages.zone).toLowerCase() }}
                            >
                                <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                    <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                    <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                </Button>
                            </DeleteZoneFormModal>
                        </div>
                    )}
                </div>
            </SectionBlock>

            <GeneralBlock>
                <div className="grid flex-3 grid-cols-3 gap-4">
                    {zoneData?.translations.map((translation) => {
                        const languageToUpper = translation.language.toUpperCase();
                        return (
                            <GeneralBlockAttribute
                                label={`${formatMessage(generalMessages.name)} (${languageToUpper})`}
                                value={translation.name}
                                key={translation.id}
                            />
                        );
                    })}
                </div>
                <Separator />
                <div className="grid flex-3 grid-cols-3 gap-4">
                    <GeneralBlockAttribute label={formatMessage(zoneMessages.type)}>
                        {zoneData?.type && (
                            <Badge theme={zoneTypeBadgeOption?.theme ?? 'gray'} className="max-h-fit">
                                {zoneTypeBadgeOption?.icon && <FontAwesomeIcon icon={zoneTypeBadgeOption.icon} />}
                                {formatMessage(
                                    zoneTypeBadgeOption?.label ?? {
                                        id: 'unknown',
                                        defaultMessage: zoneData?.type,
                                    },
                                )}
                            </Badge>
                        )}
                    </GeneralBlockAttribute>

                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.visitorCategories)}
                        value={
                            zoneData?.visitor_categories?.length
                                ? zoneData?.visitor_categories
                                      ?.map((c) => formatMessage(VISITOR_CATEGORIES_TRANSLATION_MAP[c]))
                                      .join(', ')
                                : '-'
                        }
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.spacesCount)}
                        value={zoneData?.spaces_count}
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.buffer)}
                        value={zoneData?.overflow_spaces}
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.totalSpaces)}
                        value={zoneData?.total_spaces}
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.site)}
                        value={
                            zoneData
                                ? zoneData.site.translations.find((translation) => translation.language === locale)
                                      ?.name
                                : '-'
                        }
                        link={accessSites && zoneData ? `/${PATH.ADMINISTRATION}/site/${zoneData?.site.id}` : undefined}
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.organisation)}
                        value={zoneData?.service_provider.name}
                    />
                    {zoneData?.parent_zone ? (
                        zoneData.parent_zone.translations.map((translation) => {
                            const languageToUpper = translation.language.toUpperCase();
                            return (
                                <GeneralBlockAttribute
                                    label={`${formatMessage(zoneMessages.parentZone)} (${languageToUpper})`}
                                    value={translation.name}
                                    link={`/${PATH.ADMINISTRATION}/zone/${zoneData.parent_zone.id}`}
                                    key={translation.id}
                                />
                            );
                        })
                    ) : (
                        <GeneralBlockAttribute label={formatMessage(zoneMessages.parentZone)} />
                    )}
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.limitAccessCapacity)}
                        value={
                            zoneData
                                ? formatMessage(
                                      BOOLEAN_MAP[zoneData.limit_access_per_capacity.toString() as BOOLEAN_TYPE],
                                  )
                                : '-'
                        }
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.limitUserAccess)}
                        value={
                            zoneData
                                ? formatMessage(BOOLEAN_MAP[zoneData.limit_user_access.toString() as BOOLEAN_TYPE])
                                : '-'
                        }
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(zoneMessages.userAccess)}
                        value={zoneData?.user_access.length ? zoneData?.user_access.map((z) => z.name).join(', ') : '-'}
                    />
                </div>
                <Separator />
                <div className="grid flex-3 grid-cols-3 gap-4">
                    {zoneData?.translations.map((translation) => {
                        const languageToUpper = translation.language.toUpperCase();
                        return (
                            <GeneralBlockAttribute
                                key={translation.id}
                                label={`${formatMessage(generalMessages.description)} (${languageToUpper})`}
                                value={translation.description?.length ? translation.description : '-'}
                            />
                        );
                    })}
                </div>
            </GeneralBlock>
            {managePricingPlans && (
                <Tabs
                    defaultValue={defaultTab}
                    value={tab}
                    onValueChange={(value) => setSearchParams({ tab: value }, { replace: true })}
                >
                    <TabsList>
                        <TabsTrigger value="pricing-plans">
                            <FormattedMessage {...pricingPlanMessages.pricingPlans} />
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="pricing-plans">
                        <ZonePricingPlans zoneId={zoneId} />
                    </TabsContent>
                </Tabs>
            )}
        </>
    );
}
